import React, { useRef, useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import NotificationDropdown, { useNotificationCounts } from './NotificationDropdown';
import { OnlineProvider } from './OnlineIndicator';
import { toast } from 'sonner';
import {
  Home, User, MessageCircle, Users, ShoppingBag, Calendar,
  LogOut, Search, Bell, Menu, X, ChevronDown, Settings as SettingsIcon,
  Sparkles, MessageSquare, Moon, Sun, Newspaper, CreditCard, Mail, LayoutDashboard, ShieldCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve avatar/media URLs
const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  if (url.includes('preview.emergentagent.com/api/uploads')) {
    const match = url.match(/\/api\/uploads\/.+/);
    if (match) return `${BACKEND_URL}${match[0]}`;
  }
  return url;
};

const requestNotificationPermission = () => {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
};
const sendBrowserNotification = (title, body) => {
  if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
    new Notification(title, { body, icon: '/favicon.ico' });
  }
};

// Persisted collapsible section
const useCollapsible = (key, defaultOpen = true) => {
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(`sidebar:${key}`);
      return stored === null ? defaultOpen : stored === '1';
    } catch { return defaultOpen; }
  });
  useEffect(() => {
    try { localStorage.setItem(`sidebar:${key}`, open ? '1' : '0'); } catch {}
  }, [key, open]);
  return [open, setOpen];
};

const NavButton = ({ active, onClick, icon: Icon, label, badge, badgeDot, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all ${
      active
        ? 'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white shadow shadow-purple-200/60 dark:shadow-purple-900/40'
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
    }`}
  >
    <span className="flex items-center gap-3 min-w-0">
      <Icon className="h-4 w-4 shrink-0" />
      <span className="font-medium truncate">{label}</span>
    </span>
    {/* Sprint 1.0c V1.a-bis hotfix #4 (Bug 2): badge (count) OR dot (presence only) */}
    {badge ? (
      <span
        data-testid={`${testid}-badge`}
        className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/20' : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'}`}
      >
        {badge > 9 ? '9+' : badge}
      </span>
    ) : badgeDot ? (
      <span
        data-testid={`${testid}-dot`}
        className={`h-2 w-2 rounded-full ${active ? 'bg-white' : 'bg-gradient-to-r from-cyan-400 to-pink-500 animate-pulse shadow-[0_0_6px_rgba(236,72,153,0.8)]'}`}
        title="Activité récente"
      />
    ) : null}
  </button>
);

const SectionHeader = ({ label, open, onToggle, testid }) => (
  <button
    onClick={onToggle}
    data-testid={testid}
    className="w-full flex items-center justify-between px-3 py-1.5 mt-1 text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
  >
    <span>{label}</span>
    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`} />
  </button>
);

const AppShell = () => {
  const { user, logout } = useAuth();
  const { isDark, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { counts: notifCounts, refresh: refreshNotifications } = useNotificationCounts();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [spaceOpen, setSpaceOpen] = useCollapsible('my-space', true);
  const [communityOpen, setCommunityOpen] = useCollapsible('community', true);

  const generalRef = useRef(null);
  const messagesRef = useRef(null);
  const communitiesRef = useRef(null);
  const avatarRef = useRef(null);
  const prevCounts = useRef({ general: 0, messages: 0, communities: 0 });

  useEffect(() => { requestNotificationPermission(); }, []);

  // Lazy-check admin status (used only for showing admin links in the avatar menu)
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/me/is-admin`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setIsAdmin(!!data.is_admin); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const prev = prevCounts.current;
    if (notifCounts.general > prev.general) {
      sendBrowserNotification('OpenSapiens', `${notifCounts.general - prev.general} nouvelle(s) notification(s)`);
    }
    if (notifCounts.messages > prev.messages) {
      sendBrowserNotification('Nouveau message', `Vous avez ${notifCounts.messages - prev.messages} nouveau(x) message(s)`);
    }
    if (notifCounts.communities > prev.communities) {
      sendBrowserNotification('Communauté', `${notifCounts.communities - prev.communities} nouvelle(s) activité(s)`);
    }
    prevCounts.current = { ...notifCounts };
  }, [notifCounts]);

  useEffect(() => {
    const checkNewMessages = async () => {
      try {
        const { data } = await axios.get(`${API}/social/messages-check`, { withCredentials: true });
        if (data.new_messages?.length > 0) {
          data.new_messages.forEach((msg) => {
            toast(msg.sender_name, {
              description: msg.content,
              action: { label: 'Voir', onClick: () => navigate(`/app/messages/${msg.sender_id}`) },
              duration: 5000,
            });
          });
        }
      } catch {}
    };
    const interval = setInterval(checkNewMessages, 15000);
    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setShowMobileMenu(false); setAvatarMenuOpen(false); }, [location.pathname]);

  // Close avatar menu on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) setAvatarMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = async () => {
    setShowMobileMenu(false);
    setAvatarMenuOpen(false);
    navigate('/');
    await logout();
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // === Navigation structure (Bug M — refonte sidebar) ===
  // Sprint 1.0c Bug M : "Accueil" fusionné avec "Dashboard" (route /app/home),
  // "Nous contacter" déplacé en bas de sidebar hors flux principal, 3 sections
  // visuelles (Cœur sans label / MON ESPACE / COMMUNAUTÉ).
  const coreItems = [
    { path: '/app/home', icon: LayoutDashboard, label: 'Accueil', testid: 'nav-home' },
    { path: '/app/discover', icon: Search, label: 'Découvrir', testid: 'nav-discover' },
    { path: '/app/messages', icon: MessageCircle, label: 'Messages', badge: notifCounts.messages, testid: 'nav-messages' },
    { path: '/app/assistant', icon: Sparkles, label: 'MaIA', testid: 'nav-assistant' },
  ];

  const spaceItems = [
    { path: `/app/profile/${user?.user_id}`, icon: User, label: 'Mon profil', testid: 'nav-profile' },
    { path: `/app/shop/${user?.user_id}`, icon: ShoppingBag, label: 'Ma boutique', testid: 'nav-shop' },
    { path: '/app/sessions', icon: Calendar, label: 'Mes sessions', badge: notifCounts.sessions, testid: 'nav-sessions' },
  ];

  const communityItems = [
    { path: '/app/feed', icon: Newspaper, label: 'Actualité', testid: 'nav-feed' },
    // Sprint 1.0c V1.a-bis hotfix #4 (Bug 2): dot only (no count) — Slack pattern.
    { path: '/app/community', icon: Users, label: 'Communautés', badgeDot: notifCounts.communities > 0, testid: 'nav-community' },
  ];

  // Mobile bottom bar: 4 items from core
  const mobileNavItems = coreItems;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-cyan-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
      {/* Top Bar */}
      <header className={`bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-700/50 sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-sm' : ''}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'h-12' : 'h-16'}`}>
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/app/home')}>
              <span className={`font-bold bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600 bg-clip-text text-transparent transition-all duration-300 ${scrolled ? 'text-lg' : 'text-xl'}`}>
                OpenSapiens
              </span>
            </div>

            {/* Right: Notifs + avatar + mobile menu */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              {/* General Notifications — Sprint 1.0c V1.a-bis hotfix #4 (Bug 2):
                  single bell in header (Slack/Discord pattern). Messages + 
                  community-activity badges live in the sidebar instead. */}
              <div className="relative">
                <button
                  ref={generalRef}
                  onClick={() => { const next = openDropdown === 'general' ? null : 'general'; setOpenDropdown(next); if (next) refreshNotifications(); }}
                  className={`relative text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-xl transition-all ${scrolled ? 'p-1.5' : 'p-2'}`}
                  data-testid="notifications-general-btn"
                  title="Notifications"
                >
                  <Bell className={`transition-all ${scrolled ? 'h-4 w-4' : 'h-5 w-5'}`} />
                  {notifCounts.general > 0 && (
                    <span data-testid="bell-badge" className="absolute -top-1 -right-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                      {notifCounts.general > 9 ? '9+' : notifCounts.general}
                    </span>
                  )}
                </button>
                <NotificationDropdown isOpen={openDropdown === 'general'} onClose={() => setOpenDropdown(null)} anchorRef={generalRef} category="general" />
              </div>

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleTheme}
                className={`rounded-xl transition-all ${isDark ? 'text-yellow-400 hover:bg-yellow-400/10' : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'} ${scrolled ? 'p-1.5' : 'p-2'}`}
                data-testid="dark-mode-toggle"
                title={isDark ? 'Mode clair' : 'Mode sombre'}
              >
                {isDark ? <Sun className={`transition-all ${scrolled ? 'h-4 w-4' : 'h-5 w-5'}`} /> : <Moon className={`transition-all ${scrolled ? 'h-4 w-4' : 'h-5 w-5'}`} />}
              </button>

              {/* User name - desktop only */}
              <div className="hidden md:flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                <span>{user?.display_name}</span>
              </div>

              {/* Avatar + menu */}
              <div className="relative" ref={avatarRef}>
                <button
                  data-testid="avatar-menu-btn"
                  onClick={() => setAvatarMenuOpen(s => !s)}
                  className={`cursor-pointer ring-2 ring-purple-100 dark:ring-purple-900/40 hover:ring-purple-300 rounded-full transition-all inline-block ${scrolled ? 'h-8 w-8' : 'h-10 w-10'}`}
                  aria-label="Menu compte"
                >
                  <Avatar className={`${scrolled ? 'h-8 w-8' : 'h-10 w-10'}`}>
                    <AvatarImage src={resolveUrl(user?.avatar)} />
                    <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-pink-500 text-white font-semibold text-sm">
                      {user?.display_name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                </button>
                {avatarMenuOpen && (
                  <div
                    data-testid="avatar-menu-dropdown"
                    className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
                  >
                    <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user?.display_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <button data-testid="avatar-menu-subscription" onClick={() => navigate('/app/subscription')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <CreditCard className="h-4 w-4" /> Mon abonnement
                      </button>
                      <button data-testid="avatar-menu-settings" onClick={() => navigate('/app/settings')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                        <SettingsIcon className="h-4 w-4" /> Paramètres
                      </button>
                      {isAdmin && (
                        <>
                          {/* Brief B4 §1 — beta dashboard link */}
                          <button data-testid="avatar-menu-admin-dashboard" onClick={() => navigate('/app/admin')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20">
                            <ShieldCheck className="h-4 w-4" /> Dashboard admin
                          </button>
                          <button data-testid="avatar-menu-admin" onClick={() => navigate('/app/admin/weekly-picks')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20">
                            <ShieldCheck className="h-4 w-4" /> Sélection hebdo (admin)
                          </button>
                        </>
                      )}
                      <button data-testid="avatar-menu-logout" onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                        <LogOut className="h-4 w-4" /> Déconnexion
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all"
                data-testid="mobile-menu-btn"
              >
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Slide-out Menu */}
      {showMobileMenu && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setShowMobileMenu(false)} />
          <div className="fixed top-0 right-0 h-full w-80 max-w-[90vw] bg-white dark:bg-gray-800 shadow-xl z-50 md:hidden animate-slide-in-right overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl mb-4">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={resolveUrl(user?.avatar)} />
                  <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-pink-500 text-white font-semibold">
                    {user?.display_name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white truncate">{user?.display_name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                </div>
              </div>

              <nav className="space-y-1">
                {/* Core */}
                {coreItems.map((item) => (
                  <NavButton key={item.path} active={isActive(item.path)} onClick={() => navigate(item.path)} icon={item.icon} label={item.label} badge={item.badge} badgeDot={item.badgeDot} testid={`m-${item.testid}`} />
                ))}

                {/* Mon espace */}
                <SectionHeader label="Mon espace" open={spaceOpen} onToggle={() => setSpaceOpen(o => !o)} testid="m-section-space" />
                {spaceOpen && spaceItems.map((item) => (
                  <NavButton key={item.path} active={isActive(item.path)} onClick={() => navigate(item.path)} icon={item.icon} label={item.label} badge={item.badge} badgeDot={item.badgeDot} testid={`m-${item.testid}`} />
                ))}

                {/* Communauté */}
                <SectionHeader label="Communauté" open={communityOpen} onToggle={() => setCommunityOpen(o => !o)} testid="m-section-community" />
                {communityOpen && communityItems.map((item) => (
                  <NavButton key={item.path} active={isActive(item.path)} onClick={() => navigate(item.path)} icon={item.icon} label={item.label} badge={item.badge} badgeDot={item.badgeDot} testid={`m-${item.testid}`} />
                ))}
              </nav>

              <div className="my-4 border-t border-gray-100 dark:border-gray-700" />

              {/* Compte */}
              <div className="space-y-1">
                <NavButton active={isActive('/app/subscription')} onClick={() => navigate('/app/subscription')} icon={CreditCard} label="Mon abonnement" testid="m-nav-subscription" />
                <NavButton active={isActive('/app/settings')} onClick={() => navigate('/app/settings')} icon={SettingsIcon} label="Paramètres" testid="m-nav-settings" />
                <NavButton active={isActive('/app/contact')} onClick={() => navigate('/app/contact')} icon={Mail} label="Nous contacter" testid="m-nav-contact" />
              </div>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all mt-4"
                data-testid="m-nav-logout"
              >
                <LogOut className="h-5 w-5" />
                <span className="font-medium">Déconnexion</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Main */}
      <div className="max-w-7xl mx-auto px-4 py-6 pb-32 md:pb-6">
        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-64 shrink-0">
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg shadow-purple-100/50 dark:shadow-gray-900/50 border border-purple-100/50 dark:border-gray-700/50 p-3 sticky top-20 flex flex-col" style={{ minHeight: 'calc(100vh - 6rem)' }} data-testid="sidebar">
              <nav className="space-y-1">
                {/* === Section 1 — Cœur (sans label) === */}
                {coreItems.map((item) => (
                  <NavButton key={item.path} active={isActive(item.path)} onClick={() => navigate(item.path)} icon={item.icon} label={item.label} badge={item.badge} badgeDot={item.badgeDot} testid={item.testid} />
                ))}

                <div className="my-2 border-t border-gray-100 dark:border-gray-700/70" />

                {/* === Section 2 — MON ESPACE === */}
                <SectionHeader label="Mon espace" open={spaceOpen} onToggle={() => setSpaceOpen(o => !o)} testid="section-space-toggle" />
                {spaceOpen && spaceItems.map((item) => (
                  <NavButton key={item.path} active={isActive(item.path)} onClick={() => navigate(item.path)} icon={item.icon} label={item.label} badge={item.badge} badgeDot={item.badgeDot} testid={item.testid} />
                ))}

                <div className="my-2 border-t border-gray-100 dark:border-gray-700/70" />

                {/* === Section 3 — COMMUNAUTÉ === */}
                <SectionHeader label="Communauté" open={communityOpen} onToggle={() => setCommunityOpen(o => !o)} testid="section-community-toggle" />
                {communityOpen && communityItems.map((item) => (
                  <NavButton key={item.path} active={isActive(item.path)} onClick={() => navigate(item.path)} icon={item.icon} label={item.label} badge={item.badge} badgeDot={item.badgeDot} testid={item.testid} />
                ))}
              </nav>

              {/* === Footer (Bug M — Nous contacter en bas, hors flux principal) === */}
              <div className="mt-auto pt-3">
                <div className="border-t border-gray-100 dark:border-gray-700/70 mb-2" />
                <NavButton active={isActive('/app/contact')} onClick={() => navigate('/app/contact')} icon={Mail} label="Nous contacter" testid="nav-contact" />
                {/* Brief B4 §1 — lien discret Admin (visible uniquement aux ADMIN_EMAILS) */}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => navigate('/app/admin')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 mt-1 rounded-lg text-[12px] transition-colors ${
                      isActive('/app/admin')
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                    data-testid="nav-admin"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" /> Admin
                  </button>
                )}

                {/* Legal links — sprint 1.0a (RGPD) */}
                <div className="px-3 pt-3 pb-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500">
                  <a href="/legal/cgu" data-testid="nav-legal-cgu" className="hover:text-purple-600 dark:hover:text-purple-300">CGU</a>
                  <a href="/legal/privacy" data-testid="nav-legal-privacy" className="hover:text-purple-600 dark:hover:text-purple-300">Confidentialité</a>
                  <a href="/legal/mentions" data-testid="nav-legal-mentions" className="hover:text-purple-600 dark:hover:text-purple-300">Mentions</a>
                </div>
              </div>
            </div>
          </div>

          {/* Main */}
          <div className="flex-1 max-w-4xl mx-auto w-full">
            <OnlineProvider>
              <Outlet />
            </OnlineProvider>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200/50 dark:border-gray-700/50 z-40 md:hidden" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 50px)' }}>
        <div className="flex items-center justify-around h-16 px-2">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const badge = item.badge && item.badge > 0 ? item.badge : null;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center flex-1 py-2 px-1 rounded-xl transition-all relative ${active ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                data-testid={`mobile-${item.testid}`}
              >
                <div className="relative">
                  <Icon className={`h-6 w-6 transition-transform ${active ? 'scale-110' : ''}`} />
                  {active && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-purple-600 rounded-full" />}
                  {badge && (
                    <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </div>
                <span className={`text-xs mt-1 font-medium transition-all ${active ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
          <button
            onClick={() => setShowMobileMenu(true)}
            className="flex flex-col items-center justify-center flex-1 py-2 px-1 rounded-xl text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-all"
            data-testid="mobile-nav-more"
          >
            <Menu className="h-6 w-6" />
            <span className="text-xs mt-1 font-medium">Plus</span>
          </button>
        </div>
      </nav>

      <style jsx>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default AppShell;
